import * as stylex from '@stylexjs/stylex'
import { useId, useState } from 'react'

import Avatar from './Avatar'
import Dialog from './Dialog'
import Icon from './Icon'
import Button from './Button'
import { FormField, Textarea } from './FormControls'
import MoneyInput from './MoneyInput'
import TransactionDateTimeField from './TransactionDateTimeField'
import { centsToInput, combineTransactionDateTime, findPerson, formatMoney, localDateInputValue, parseCents, type Balance, type CreateSettlementInput, type Overview } from '../lib/ledger'


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#dce9da",
    backgroundColor: "#f5faf3",
    padding: "16px",
  },
  s2: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  s3: {
    fontSize: "12px",
    color: "#647267",
  },
  s4: {
    display: "block",
  },
  s5: {
    marginBottom: "8px",
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s6: {
    position: "relative",
  },
  s7: {
    pointerEvents: "none",
    position: "absolute",
    insetBlock: "0px",
    left: "14px",
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#68806d",
  },
  s8: {
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingLeft: "49px",
    paddingRight: "14px",
    fontSize: "18px",
    fontWeight: 600,
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      fontWeight: 400,
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s9: {
    marginTop: "8px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#397a4c",
    ":hover": {
      textDecoration: "underline",
    },
  },
  s10: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#f0d0c0",
    backgroundColor: "#fff5f0",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    color: "#9b491e",
  },
  s11: {
    display: "flex",
    flexDirection: "column-reverse",
    gap: "12px",
    borderTopWidth: 1,
    borderColor: "#e8ece7",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      justifyContent: "end",
    },
  },
  s12: {
    height: "44px",
    borderRadius: "0.75rem",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#607068",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f4ef",
    },
  },
  s13: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  s14: {
    height: "16px",
    width: "16px",
  },
  s15: {
    display: "grid",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#f8faf7",
    padding: "14px",
  },
  s16: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    fontSize: "12px",
    color: "#748077",
  },
  s17: {
    fontWeight: 600,
    color: "#3d5542",
  },
  quickAmounts: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "10px",
  },
  quickAmount: {
    minHeight: "32px",
    borderRadius: "9999px",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "11px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#607068",
    ":hover": { backgroundColor: "#f2f8f1", color: "#3e7d4d" },
  },
  dateTimeCard: {
    borderRadius: "0.875rem",
    borderWidth: 1,
    borderColor: "#e2e8e0",
    backgroundColor: "#fbfcfa",
    padding: "14px",
  },
})

const settlementStyles = stylex.create({
  amount: { marginTop: '2px', fontSize: '18px', fontWeight: 600, letterSpacing: '-.02em' },
  received: { color: '#287441' },
  sent: { color: '#a9501b' },
})
type SettlementDialogProps = {
  balance: Balance
  data: Overview
  onClose: () => void
  onSave: (input: CreateSettlementInput) => Promise<void>
}

export default function SettlementDialog({ balance, data, onClose, onSave }: SettlementDialogProps) {
  const formID = useId()
  const [description, setDescription] = useState('')
  const person = findPerson(data, balance.personId)
  const maximum = Math.abs(balance.amountCents)
  const direction: 'paid' | 'received' = balance.amountCents > 0 ? 'received' : 'paid'
  const [amount, setAmount] = useState(centsToInput(maximum))
  const [paymentDate, setPaymentDate] = useState(() => localDateInputValue(new Date()))
  const [paymentTime, setPaymentTime] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const amountCents = parseCents(amount)
  const valid = amountCents !== null && amountCents > 0 && amountCents <= maximum && Boolean(paymentDate)
  const remaining = valid && amountCents !== null ? maximum - amountCents : maximum

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!valid || amountCents === null) {
      setError(`Enter a date and an amount up to ${formatMoney(maximum, balance.currency)}.`)
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave({
        description: description.trim(),
        personId: balance.personId,
        amountCents,
        direction,
        currency: balance.currency,
        paidAt: combineTransactionDateTime(paymentDate, paymentTime),
        paidTimeSet: Boolean(paymentTime),
      })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'We could not record that payment.')
      setSaving(false)
    }
  }

  if (!person) {
    return null
  }

  const action = direction === 'received' ? 'Record payment received' : 'Record payment sent'
  const sentence = direction === 'received' ? `${person.name} owes you` : `You owe ${person.name}`

  return (
    <Dialog description="Record money that has already changed hands. This does not move money for you." closeDisabled={saving} onClose={onClose} title={action} footer={<div {...stylex.props(styles.s11)}><Button disabled={saving} onClick={onClose} variant="secondary">Cancel</Button><Button disabled={!valid || saving} form={formID} type="submit">{saving ? 'Recording…' : 'Record payment'}<Icon name="check" /></Button></div>}>
      <form id={formID} {...stylex.props(styles.s0)} onSubmit={submit}>
        <div {...stylex.props(styles.s1)}><div {...stylex.props(styles.s2)}><Avatar person={person} size={42} /><div><p {...stylex.props(styles.s3)}>{sentence}</p><p {...stylex.props(settlementStyles.amount, direction === 'received' ? settlementStyles.received : settlementStyles.sent)}>{formatMoney(maximum, balance.currency)}</p></div></div></div>
        <label {...stylex.props(styles.s4)}><span {...stylex.props(styles.s5)}>Amount {direction === 'received' ? 'received' : 'paid'}</span><div {...stylex.props(styles.s6)}><span {...stylex.props(styles.s7)}>{balance.currency}</span><MoneyInput data-dialog-autofocus disabled={saving} onValueChange={setAmount} style={{ paddingLeft: '49px', minHeight: '48px', fontSize: '18px', fontWeight: 600 }} value={amount} /></div><button {...stylex.props(styles.s9)} onClick={() => setAmount(centsToInput(maximum))} type="button">Settle full {formatMoney(maximum, balance.currency)}</button><div {...stylex.props(styles.quickAmounts)}>{[25, 50, 75].map((percentage) => <button {...stylex.props(styles.quickAmount)} key={percentage} onClick={() => setAmount(centsToInput(Math.max(1, Math.round(maximum * percentage / 100))))} type="button">{percentage}%</button>)}<button {...stylex.props(styles.quickAmount)} onClick={() => setAmount(centsToInput(maximum))} type="button">Full</button></div></label>
        <div {...stylex.props(styles.dateTimeCard)}>
          <TransactionDateTimeField
            date={paymentDate}
            dateLabel="Payment date"
            onDateChange={setPaymentDate}
            onTimeChange={setPaymentTime}
            time={paymentTime}
            timeLabel="Payment time"
          />
        </div>
        <div {...stylex.props(styles.s15)}><div {...stylex.props(styles.s16)}><span>Current balance</span><span {...stylex.props(styles.s17)}>{formatMoney(maximum, balance.currency)}</span></div><div {...stylex.props(styles.s16)}><span>After this payment</span><span {...stylex.props(styles.s17)}>{remaining === 0 ? 'Settled up' : formatMoney(remaining, balance.currency)}</span></div></div>
        <FormField label="Description (optional)"><Textarea disabled={saving} maxLength={2000} onChange={(event) => setDescription(event.target.value)} placeholder="For example, cash repayment for dinner" value={description} /></FormField>
        {error && <p {...stylex.props(styles.s10)} role="alert">{error}</p>}
      </form>
    </Dialog>
  )
}
